import { useState, useCallback, useRef, useEffect } from "react";
import * as XLSX from "xlsx";
import s from "./FinancialCompareModal.module.css";

// ─── pure helpers ──────────────────────────────────────────────────────────────

function parseAmt(v) {
  if (v == null || v === "") return 0;
  if (typeof v === "number") return v;
  return parseFloat(String(v).replace(/[₱,\s]/g, "")) || 0;
}

function fmt(n) {
  return Number(n || 0).toLocaleString("en-PH", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
}

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d) ? "—" : d.toLocaleDateString("en-PH", {
    year: "numeric", month: "short", day: "numeric",
  });
}

function buildSysName(stu) {
  if (!stu) return "";
  return `${stu.firstName || ""} ${stu.lastName || ""}`.trim();
}

function nameWords(str) {
  return String(str || "")
    .toLowerCase()
    .replace(/[,\.\-]/g, " ")
    .split(/\s+/)
    .filter(w => w.length > 1);
}

function namesMatch(a, b) {
  const wa = nameWords(a);
  const wb = nameWords(b);
  if (!wa.length || !wb.length) return false;
  const common = wa.filter(w => wb.includes(w));
  return common.length >= Math.min(2, Math.min(wa.length, wb.length));
}

// ─── Excel sheet parser ────────────────────────────────────────────────────────

const SKIP_PATTERNS = [
  "name of learner", "grade level", "particulars", "grand total",
  "less expense", "net for deposit", "expenses", "description",
  "total expenses", "prepared by", "received by", "sales report",
  "amount", "reference number", "remarks",
];

function parseSheet(aoa, sheetName) {
  const rows = [];
  for (const raw of aoa) {
    const row = raw || [];
    const colA = row[0];
    if (colA == null || colA === "") continue;
    const aStr = String(colA).trim();
    if (!aStr) continue;
    const aLow = aStr.toLowerCase();
    if (SKIP_PATTERNS.some(p => aLow.includes(p))) continue;

    // Require an OR# in column D (index 3)
    const colD = row[3];
    if (colD == null || colD === "") continue;
    const orNum = String(colD).trim();
    if (!orNum || isNaN(Number(orNum))) continue;

    const cash   = parseAmt(row[4]);
    const online = parseAmt(row[5]);
    const total  = cash + online;
    if (total < 0.005) continue;

    rows.push({
      orNum,
      studentName: aStr,
      gradeLevel:  row[1] != null ? String(row[1]).trim() : "—",
      particulars: row[2] != null ? String(row[2]).trim() : "—",
      cash,
      online,
      total,
      reference:  row[6] != null ? String(row[6]).trim() : "",
      remarks:    row[7] != null ? String(row[7]).trim() : "",
      sheetName,
    });
  }
  return rows;
}

// ─── comparison engine ─────────────────────────────────────────────────────────

function runComparison(excelRows, payments, invoices, students) {
  const stuById = new Map(students.map(x => [String(x.id), x]));
  const invById = new Map(invoices.map(x => [String(x.id), x]));
  const payById = new Map(payments.map(x => [String(x.id), x]));

  const getSysName = p => {
    const inv = invById.get(String(p.invoiceId));
    const stu = inv ? stuById.get(String(inv.studentId)) : null;
    return buildSysName(stu);
  };

  const matchedSysIds = new Set();
  const results = [];

  for (const row of excelRows) {
    const byId = payById.get(row.orNum);

    if (byId) {
      // ── primary match: OR# = payment ID ────────────────────────────────
      matchedSysIds.add(row.orNum);
      const sysAmt = Number(byId.amount || 0);
      const delta  = row.total - sysAmt;
      results.push({
        key:             row.orNum + "_" + row.studentName,
        status:          Math.abs(delta) < 0.005 ? "matched" : "mismatch",
        matchedBy:       "OR#",
        excelRow:        row,
        sysId:           byId.id,
        sysName:         getSysName(byId),
        sysAmt,
        sysDateReceived: byId.paymentReceivedOn || "",
        sysDateRecorded: byId.paidAt            || "",
        sysMethod:       byId.method || "—",
        delta,
      });
    } else {
      // ── fallback match: name similarity ────────────────────────────────
      let best = null;
      for (const p of payments) {
        if (matchedSysIds.has(String(p.id))) continue;
        const nm  = getSysName(p);
        if (!namesMatch(row.studentName, nm)) continue;
        const diff = Math.abs(row.total - Number(p.amount || 0));
        if (!best || diff < best.diff) best = { p, nm, diff };
      }

      if (best && best.diff < 1) {
        matchedSysIds.add(String(best.p.id));
        const sysAmt = Number(best.p.amount || 0);
        const delta  = row.total - sysAmt;
        results.push({
          key:             row.orNum + "_" + row.studentName,
          status:          Math.abs(delta) < 0.005 ? "matched" : "mismatch",
          matchedBy:       "Name",
          excelRow:        row,
          sysId:           best.p.id,
          sysName:         best.nm,
          sysAmt,
          sysDateReceived: best.p.paymentReceivedOn || "",
          sysDateRecorded: best.p.paidAt            || "",
          sysMethod:       best.p.method || "—",
          delta,
        });
      } else {
        results.push({
          key:             row.orNum + "_" + row.studentName,
          status:          "missing_sys",
          matchedBy:       null,
          excelRow:        row,
          sysId:           null,
          sysName:         "—",
          sysAmt:          0,
          sysDateReceived: "",
          sysDateRecorded: "",
          sysMethod:       "—",
          delta:           row.total,
        });
      }
    }
  }

  // system payments not found in the Excel at all
  for (const p of payments) {
    if (matchedSysIds.has(String(p.id))) continue;
    const sysAmt = Number(p.amount || 0);
    results.push({
      key:             "sys_" + p.id,
      status:          "extra_sys",
      matchedBy:       null,
      excelRow:        null,
      sysId:           p.id,
      sysName:         getSysName(p),
      sysAmt,
      sysDateReceived: p.paymentReceivedOn || "",
      sysDateRecorded: p.paidAt            || "",
      sysMethod:       p.method || "—",
      delta:           -sysAmt,
    });
  }

  const excelTotal  = excelRows.reduce((a, r) => a + r.total, 0);
  const sysTotal    = payments.reduce((a, p) => a + Number(p.amount || 0), 0);

  return {
    rows: results,
    summary: {
      matched:    results.filter(r => r.status === "matched").length,
      mismatch:   results.filter(r => r.status === "mismatch").length,
      missingSys: results.filter(r => r.status === "missing_sys").length,
      extraSys:   results.filter(r => r.status === "extra_sys").length,
      excelTotal,
      sysTotal,
      totalDelta: excelTotal - sysTotal,
    },
  };
}

// ─── status config ─────────────────────────────────────────────────────────────

const STATUS = {
  matched:     { label: "Matched",            color: "#1b5e20", bg: "rgba(46,125,50,0.09)",  border: "rgba(46,125,50,0.25)",  dot: "#2e7d32" },
  mismatch:    { label: "Amount Mismatch",     color: "#7d4e00", bg: "rgba(224,124,0,0.09)",  border: "rgba(224,124,0,0.28)",  dot: "#e07c00" },
  missing_sys: { label: "Missing in System",   color: "#7f0000", bg: "rgba(192,57,43,0.09)",  border: "rgba(192,57,43,0.25)",  dot: "#c0392b" },
  extra_sys:   { label: "Not in Your Excel",   color: "#0d3c6e", bg: "rgba(58,116,212,0.09)", border: "rgba(58,116,212,0.25)", dot: "#3a74d4" },
};

// ─── animated counter ──────────────────────────────────────────────────────────

// Checks if the system date's month roughly matches the Excel context string.
function isDateMismatch(iso, excelContext) {
  if (!iso || !excelContext) return false;
  const d = new Date(iso);
  if (isNaN(d)) return false;
  const longMon  = d.toLocaleString("en-US", { month: "long"  }).toLowerCase();
  const shortMon = d.toLocaleString("en-US", { month: "short" }).toLowerCase();
  const ctx = excelContext.toLowerCase();
  return !ctx.includes(longMon) && !ctx.includes(shortMon);
}

function DateCell({ iso, excelContext, label }) {
  if (!iso) return <td className={s.td}><span className={s.muted}>—</span></td>;
  const d = new Date(iso);
  const formatted = isNaN(d) ? iso : d.toLocaleDateString("en-PH", {
    year: "numeric", month: "short", day: "numeric",
  });
  const mismatch = label === "received" && isDateMismatch(iso, excelContext);
  return (
    <td className={`${s.td} ${mismatch ? s.dateMismatch : ""}`} title={mismatch ? "Month may not match your Excel" : undefined}>
      {formatted}
      {mismatch && <span className={s.dateWarn} aria-label="Date month mismatch"> ⚠</span>}
    </td>
  );
}

function AnimatedCount({ value }) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    let frame;
    const start = performance.now();
    const dur = 700;
    function tick(now) {
      const p = Math.min((now - start) / dur, 1);
      const ease = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(ease * value));
      if (p < 1) frame = requestAnimationFrame(tick);
    }
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [value]);
  return display;
}

// ─── main component ────────────────────────────────────────────────────────────

export default function FinancialCompareModal({
  onClose, payments, invoices, students, activeSy, activeSem,
}) {
  const [step,        setStep]        = useState("upload");  // upload | sheets | results
  const [dragging,    setDragging]    = useState(false);
  const [fileName,    setFileName]    = useState("");
  const [sheets,      setSheets]      = useState([]);
  const [selected,    setSelected]    = useState(new Set());
  const [comparison,  setComparison]  = useState(null);
  const [filter,      setFilter]      = useState("all");
  const [sortKey,     setSortKey]     = useState("status");
  const [sortDir,     setSortDir]     = useState("asc");
  const [loading,     setLoading]     = useState(false);
  const [parseError,  setParseError]  = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const fileInputRef = useRef(null);

  const scopeLabel = [activeSy?.label, activeSem?.label].filter(Boolean).join(" · ") || "All data";

  // close on Escape
  useEffect(() => {
    const fn = e => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", fn);
    return () => document.removeEventListener("keydown", fn);
  }, [onClose]);

  // ── file processing ──────────────────────────────────────────────────────
  const processFile = useCallback((file) => {
    setParseError("");
    if (!file) return;
    if (!/\.(xlsx|xls|xlsm)$/i.test(file.name)) {
      setParseError("Please upload an Excel file (.xlsx, .xls, or .xlsm).");
      return;
    }
    setLoading(true);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(new Uint8Array(e.target.result), { type: "array" });
        const SKIP_SHEETS = new Set(["template", "unrecorded"]);
        const parsed = wb.SheetNames
          .filter(n => !SKIP_SHEETS.has(n.toLowerCase()))
          .map(n => {
            const ws   = wb.Sheets[n];
            const aoa  = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null });
            const rows = parseSheet(aoa, n);
            return { name: n, rows, count: rows.length };
          })
          .filter(sh => sh.count > 0);

        if (!parsed.length) {
          setParseError("No payment rows were detected. Make sure the file follows the Sales Report format (OR# in column D, amount in column E or F).");
          setLoading(false);
          return;
        }
        setFileName(file.name);
        setSheets(parsed);
        setSelected(new Set(parsed.map(sh => sh.name)));
        setStep("sheets");
      } catch {
        setParseError("Could not read the file. Please make sure it is a valid Excel file.");
      } finally {
        setLoading(false);
      }
    };
    reader.onerror = () => {
      setParseError("File read failed. Please try again.");
      setLoading(false);
    };
    reader.readAsArrayBuffer(file);
  }, []);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    processFile(e.dataTransfer.files[0]);
  }, [processFile]);

  const onDragOver = (e) => { e.preventDefault(); setDragging(true); };
  const onDragLeave = () => setDragging(false);

  // ── run comparison ───────────────────────────────────────────────────────
  function compare() {
    setLoading(true);
    const excelRows = sheets
      .filter(sh => selected.has(sh.name))
      .flatMap(sh => sh.rows);

    setTimeout(() => {
      const result = runComparison(excelRows, payments, invoices, students);
      setComparison(result);
      setStep("results");
      setLoading(false);
    }, 80);
  }

  // ── sorting ──────────────────────────────────────────────────────────────
  function toggleSort(key) {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  }

  // ── filtered + sorted rows ───────────────────────────────────────────────
  const visibleRows = comparison
    ? [...comparison.rows]
        .filter(r => filter === "all" || r.status === filter)
        .filter(r => {
          if (!searchQuery.trim()) return true;
          const q = searchQuery.trim().toLowerCase();
          return (
            (r.excelRow?.studentName || "").toLowerCase().includes(q) ||
            (r.sysName || "").toLowerCase().includes(q) ||
            String(r.excelRow?.orNum || r.sysId || "").toLowerCase().includes(q) ||
            (r.excelRow?.gradeLevel || "").toLowerCase().includes(q) ||
            (r.excelRow?.particulars || "").toLowerCase().includes(q)
          );
        })
        .sort((a, b) => {
          let av, bv;
          if (sortKey === "status")  { av = a.status;  bv = b.status; }
          else if (sortKey === "name")   { av = (a.excelRow?.studentName || a.sysName || "").toLowerCase(); bv = (b.excelRow?.studentName || b.sysName || "").toLowerCase(); }
          else if (sortKey === "or")     { av = Number(a.excelRow?.orNum || a.sysId || 0); bv = Number(b.excelRow?.orNum || b.sysId || 0); }
          else if (sortKey === "excel")  { av = a.excelRow?.total || 0; bv = b.excelRow?.total || 0; }
          else if (sortKey === "sys")    { av = a.sysAmt; bv = b.sysAmt; }
          else if (sortKey === "delta")  { av = Math.abs(a.delta); bv = Math.abs(b.delta); }
          else { av = 0; bv = 0; }
          const cmp = typeof av === "string" ? av.localeCompare(bv) : av - bv;
          return sortDir === "asc" ? cmp : -cmp;
        })
    : [];

  const SortIcon = ({ col }) => (
    <span className={s.sortIcon} aria-hidden="true">
      {sortKey === col ? (sortDir === "asc" ? " ▲" : " ▼") : " ⇅"}
    </span>
  );

  // ── export comparison as Excel ───────────────────────────────────────────
  function exportComparison() {
    if (!comparison) return;
    const wb2 = XLSX.utils.book_new();

    // Summary sheet
    const sumAoa = [
      ["COMPARISON REPORT"],
      [`System scope: ${scopeLabel}`],
      [`Excel file: ${fileName}`],
      [`Generated: ${new Date().toLocaleString("en-PH")}`],
      [],
      ["Metric", "Count / Amount"],
      ["Matched (exact)",          comparison.summary.matched],
      ["Amount Mismatch",          comparison.summary.mismatch],
      ["Missing in System",        comparison.summary.missingSys],
      ["Not in Your Excel",        comparison.summary.extraSys],
      [],
      ["Your Excel Total (PHP)",   comparison.summary.excelTotal],
      ["System Total (PHP)",       comparison.summary.sysTotal],
      ["Difference (PHP)",         comparison.summary.totalDelta],
    ];
    const wsSummary = XLSX.utils.aoa_to_sheet(sumAoa);
    wsSummary["!cols"] = [{ wch: 30 }, { wch: 20 }];
    XLSX.utils.book_append_sheet(wb2, wsSummary, "Summary");

    // Detail sheet
    const header = [
      "Status", "Matched By", "OR #",
      "Excel: Student", "Excel: Grade", "Excel: Month", "Excel: Sheet Date",
      "Excel: Cash", "Excel: Online", "Excel: Total",
      "System: ID", "System: Student", "System: Amount", "System: Method",
      "Sys Date Received", "Sys Date Recorded (Input)",
      "Difference",
    ];
    const detailAoa = [header];
    for (const r of comparison.rows) {
      detailAoa.push([
        STATUS[r.status]?.label || r.status,
        r.matchedBy || "—",
        r.excelRow?.orNum || r.sysId || "—",
        r.excelRow?.studentName || "—",
        r.excelRow?.gradeLevel  || "—",
        r.excelRow?.particulars || "—",
        r.excelRow?.sheetName   || "—",
        r.excelRow?.cash   || 0,
        r.excelRow?.online || 0,
        r.excelRow?.total  || 0,
        r.sysId   || "—",
        r.sysName || "—",
        r.sysAmt,
        r.sysMethod,
        r.sysDateReceived ? fmtDate(r.sysDateReceived) : "—",
        r.sysDateRecorded ? fmtDate(r.sysDateRecorded) : "—",
        r.delta,
      ]);
    }
    const wsDetail = XLSX.utils.aoa_to_sheet(detailAoa);
    wsDetail["!cols"] = [
      { wch: 22 }, { wch: 12 }, { wch: 10 },
      { wch: 28 }, { wch: 12 }, { wch: 14 }, { wch: 16 }, { wch: 12 }, { wch: 12 }, { wch: 14 },
      { wch: 12 }, { wch: 28 }, { wch: 14 }, { wch: 14 }, { wch: 20 }, { wch: 24 },
      { wch: 14 },
    ];
    XLSX.utils.book_append_sheet(wb2, wsDetail, "Detail");

    XLSX.writeFile(wb2, `Comparison_${new Date().toISOString().slice(0, 10)}.xlsx`);
  }

  // ── render helpers ───────────────────────────────────────────────────────

  function StepDots() {
    const steps = ["upload", "sheets", "results"];
    const idx = steps.indexOf(step);
    return (
      <div className={s.steps}>
        {steps.map((st, i) => (
          <div key={st} className={`${s.step} ${i <= idx ? s.stepDone : ""}`}>
            <div className={s.stepDot}>{i < idx ? "✓" : i + 1}</div>
            <span className={s.stepLabel}>
              {["Upload", "Select Sheets", "Results"][i]}
            </span>
          </div>
        ))}
      </div>
    );
  }

  // ── render ────────────────────────────────────────────────────────────────

  return (
    <div className={s.backdrop}>
      <div className={s.modal} role="dialog" aria-modal="true" aria-label="Compare Excel Report">

        {/* ── header ────────────────────────────────────────────────── */}
        <div className={s.head}>
          <div className={s.headLeft}>
            <div className={s.headEyebrow}>Financial Verification</div>
            <h2 className={s.headTitle}>Compare Your Excel vs System</h2>
            <p className={s.headSub}>
              System scope: <b>{scopeLabel}</b> · All parsing is done locally — no data is uploaded to any server.
            </p>
          </div>
          <button className={s.closeBtn} onClick={onClose} aria-label="Close">✕</button>
        </div>

        <StepDots />

        <div className={s.body}>

          {/* ══════════════════════════════ STEP: UPLOAD ═══════════════ */}
          {step === "upload" && (
            <div className={s.uploadPane}>
              <div
                className={`${s.dropZone} ${dragging ? s.dropZoneOver : ""}`}
                onDrop={onDrop}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onClick={() => fileInputRef.current?.click()}
                role="button"
                tabIndex={0}
                onKeyDown={e => e.key === "Enter" && fileInputRef.current?.click()}
                aria-label="Upload Excel file"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.xlsm"
                  className={s.hiddenInput}
                  onChange={e => processFile(e.target.files[0])}
                />
                <div className={s.dropIcon} aria-hidden="true">
                  {loading ? (
                    <div className={s.spinner} />
                  ) : (
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14 2 14 8 20 8"/>
                      <line x1="12" y1="18" x2="12" y2="12"/>
                      <polyline points="9 15 12 12 15 15"/>
                    </svg>
                  )}
                </div>
                <div className={s.dropTitle}>
                  {loading ? "Reading file…" : "Drop your Sales Report Excel here"}
                </div>
                <div className={s.dropSub}>
                  {loading ? "" : "or click to browse · .xlsx, .xls, .xlsm"}
                </div>
              </div>

              {parseError && (
                <div className={s.errorBox} role="alert">
                  <span className={s.errorIcon} aria-hidden="true">⚠</span>
                  {parseError}
                </div>
              )}

              <div className={s.uploadHints}>
                <div className={s.hintCard}>
                  <div className={s.hintIcon} aria-hidden="true">🔍</div>
                  <div>
                    <div className={s.hintTitle}>What it checks</div>
                    <div className={s.hintBody}>Each OR# in your Excel is matched against payment records in the system. Names are cross-checked as a fallback.</div>
                  </div>
                </div>
                <div className={s.hintCard}>
                  <div className={s.hintIcon} aria-hidden="true">🔒</div>
                  <div>
                    <div className={s.hintTitle}>100% local</div>
                    <div className={s.hintBody}>Your Excel file is parsed entirely in your browser. Nothing is sent to any server.</div>
                  </div>
                </div>
                <div className={s.hintCard}>
                  <div className={s.hintIcon} aria-hidden="true">📋</div>
                  <div>
                    <div className={s.hintTitle}>Expected format</div>
                    <div className={s.hintBody}>Column A: Student Name · D: OR# · E: Cash · F: Online. TEMPLATE and UNRECORDED sheets are skipped automatically.</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ══════════════════════════ STEP: SHEET SELECT ═════════════ */}
          {step === "sheets" && (
            <div className={s.sheetPane}>
              <div className={s.sheetHeader}>
                <div className={s.sheetFile}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                       strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                  </svg>
                  <span>{fileName}</span>
                </div>
                <p className={s.sheetSub}>
                  Select which sheets to include in the comparison.
                  Sheets with no payment rows were excluded automatically.
                </p>
              </div>

              <div className={s.sheetList}>
                <label className={s.sheetCheckAll}>
                  <input
                    type="checkbox"
                    checked={selected.size === sheets.length}
                    onChange={e => setSelected(e.target.checked ? new Set(sheets.map(sh => sh.name)) : new Set())}
                  />
                  <span>{selected.size === sheets.length ? "Deselect all" : "Select all"}</span>
                </label>
                {sheets.map(sh => (
                  <label key={sh.name} className={`${s.sheetItem} ${selected.has(sh.name) ? s.sheetItemOn : ""}`}>
                    <input
                      type="checkbox"
                      checked={selected.has(sh.name)}
                      onChange={e => {
                        const next = new Set(selected);
                        e.target.checked ? next.add(sh.name) : next.delete(sh.name);
                        setSelected(next);
                      }}
                    />
                    <span className={s.sheetName}>{sh.name}</span>
                    <span className={s.sheetCount}>{sh.count} row{sh.count !== 1 ? "s" : ""}</span>
                  </label>
                ))}
              </div>

              <div className={s.sheetTotal}>
                <b>
                  {sheets.filter(sh => selected.has(sh.name)).reduce((a, sh) => a + sh.count, 0)}
                </b> payment rows selected across <b>{selected.size}</b> sheet{selected.size !== 1 ? "s" : ""}
              </div>

              <div className={s.sheetActions}>
                <button className={s.btnGhost} onClick={() => setStep("upload")}>← Back</button>
                <button
                  className={s.btnPrimary}
                  onClick={compare}
                  disabled={selected.size === 0 || loading}
                >
                  {loading ? <><div className={s.spinnerSm} /> Comparing…</> : "Run Comparison →"}
                </button>
              </div>
            </div>
          )}

          {/* ══════════════════════════ STEP: RESULTS ══════════════════ */}
          {step === "results" && comparison && (
            <div className={s.resultsPane}>

              {/* Summary cards */}
              <div className={s.summaryGrid}>
                {[
                  { key: "matched",    label: "Matched",           count: comparison.summary.matched,    color: STATUS.matched    },
                  { key: "mismatch",   label: "Amount Mismatch",   count: comparison.summary.mismatch,   color: STATUS.mismatch   },
                  { key: "missing_sys",label: "Missing in System",  count: comparison.summary.missingSys, color: STATUS.missing_sys },
                  { key: "extra_sys",  label: "Not in Your Excel", count: comparison.summary.extraSys,   color: STATUS.extra_sys  },
                ].map((c, i) => (
                  <button
                    key={c.key}
                    className={`${s.summaryCard} ${filter === c.key ? s.summaryCardActive : ""}`}
                    style={{ "--card-color": c.color.color, "--card-bg": c.color.bg, "--card-border": c.color.border, animationDelay: `${i * 0.07}s` }}
                    onClick={() => setFilter(f => f === c.key ? "all" : c.key)}
                  >
                    <div className={s.summaryCount} style={{ color: c.color.color }}>
                      <AnimatedCount value={c.count} />
                    </div>
                    <div className={s.summaryLabel}>{c.label}</div>
                    <div className={s.summaryDot} style={{ background: c.color.dot }} />
                  </button>
                ))}
              </div>

              {/* Totals banner */}
              <div className={s.totalsBanner}>
                <div className={s.totalsItem}>
                  <span className={s.totalsLabel}>Your Excel Total</span>
                  <span className={s.totalsValue}>₱{fmt(comparison.summary.excelTotal)}</span>
                </div>
                <div className={s.totalsSep} aria-hidden="true">vs</div>
                <div className={s.totalsItem}>
                  <span className={s.totalsLabel}>System Total</span>
                  <span className={s.totalsValue}>₱{fmt(comparison.summary.sysTotal)}</span>
                </div>
                <div className={s.totalsSep} aria-hidden="true">=</div>
                <div className={s.totalsItem}>
                  <span className={s.totalsLabel}>Difference</span>
                  <span className={`${s.totalsValue} ${Math.abs(comparison.summary.totalDelta) < 0.005 ? s.totalsGood : s.totalsBad}`}>
                    {comparison.summary.totalDelta >= 0 ? "+" : ""}₱{fmt(comparison.summary.totalDelta)}
                  </span>
                </div>
              </div>

              {/* Toolbar */}
              <div className={s.toolbar}>
                <div className={s.filters}>
                  {["all", "matched", "mismatch", "missing_sys", "extra_sys"].map(f => (
                    <button
                      key={f}
                      className={`${s.filterBtn} ${filter === f ? s.filterBtnOn : ""}`}
                      onClick={() => setFilter(f)}
                    >
                      {f === "all" ? `All (${comparison.rows.length})` : STATUS[f]?.label}
                    </button>
                  ))}
                </div>
                <div className={s.toolRight}>
                  <button className={s.btnGhost} onClick={() => { setStep("upload"); setComparison(null); setSheets([]); setSelected(new Set()); setFileName(""); setSearchQuery(""); }}>
                    ← New Upload
                  </button>
                  <button className={s.btnPrimary} onClick={exportComparison}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                         strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                      <polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                    </svg>
                    Export Report
                  </button>
                </div>
              </div>

              {/* Search */}
              <div className={s.searchRow}>
                <div className={s.searchWrap}>
                  <svg className={s.searchIcon} width="14" height="14" viewBox="0 0 24 24" fill="none"
                       stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <circle cx="11" cy="11" r="8"/>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                  <input
                    type="search"
                    className={s.searchInput}
                    placeholder="Search by student name, OR #, grade, month…"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    aria-label="Search comparison results"
                  />
                  {searchQuery && (
                    <button className={s.searchClear} onClick={() => setSearchQuery("")} aria-label="Clear search">✕</button>
                  )}
                </div>
                {searchQuery && (
                  <span className={s.searchHint}>
                    {visibleRows.length} result{visibleRows.length !== 1 ? "s" : ""}
                  </span>
                )}
              </div>

              {/* Table */}
              {visibleRows.length === 0 ? (
                <div className={s.emptyFilter}>No rows match the selected filter.</div>
              ) : (
                <div className={s.tableWrap}>
                  <table className={s.table}>
                    <thead>
                      <tr>
                        <th className={s.thStatus}>Status</th>
                        <th className={`${s.th} ${s.thSort}`} onClick={() => toggleSort("or")}>
                          OR # <SortIcon col="or" />
                        </th>
                        <th className={`${s.th} ${s.thSort}`} onClick={() => toggleSort("name")}>
                          Student <SortIcon col="name" />
                        </th>
                        <th className={s.th}>Grade</th>
                        <th className={s.th}>Month</th>
                        <th className={s.th}>Excel Sheet Date</th>
                        <th className={`${s.thAmt} ${s.thSort}`} onClick={() => toggleSort("excel")}>
                          Excel Amt <SortIcon col="excel" />
                        </th>
                        <th className={`${s.thAmt} ${s.thSort}`} onClick={() => toggleSort("sys")}>
                          System Amt <SortIcon col="sys" />
                        </th>
                        <th className={`${s.thAmt} ${s.thSort}`} onClick={() => toggleSort("delta")}>
                          Diff <SortIcon col="delta" />
                        </th>
                        <th className={s.th}>Sys Received</th>
                        <th className={s.th}>Sys Recorded</th>
                        <th className={s.th}>Sys ID</th>
                        <th className={s.th}>Method</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleRows.map((r, i) => {
                        const cfg = STATUS[r.status];
                        return (
                          <tr
                            key={r.key}
                            className={s.tr}
                            style={{ animationDelay: `${Math.min(i * 0.025, 0.5)}s` }}
                          >
                            <td className={s.tdStatus}>
                              <span
                                className={s.badge}
                                style={{ color: cfg.color, background: cfg.bg, borderColor: cfg.border }}
                              >
                                <span className={s.badgeDot} style={{ background: cfg.dot }} />
                                {cfg.label}
                              </span>
                              {r.matchedBy === "Name" && (
                                <span className={s.matchedByName} title="Matched by name similarity">~name</span>
                              )}
                            </td>
                            <td className={s.td}>{r.excelRow?.orNum || <span className={s.muted}>—</span>}</td>
                            <td className={s.tdName}>
                              <div className={s.nameExcel}>{r.excelRow?.studentName || <span className={s.muted}>—</span>}</div>
                              {r.sysName && r.sysName !== "—" && r.sysName !== r.excelRow?.studentName && (
                                <div className={s.nameSys}>{r.sysName}</div>
                              )}
                            </td>
                            <td className={s.td}>{r.excelRow?.gradeLevel || <span className={s.muted}>—</span>}</td>
                            <td className={s.td}>{r.excelRow?.particulars || <span className={s.muted}>—</span>}</td>
                            <td className={s.td}>{r.excelRow?.sheetName || <span className={s.muted}>—</span>}</td>
                            <td className={s.tdAmt}>
                              {r.excelRow ? `₱${fmt(r.excelRow.total)}` : <span className={s.muted}>—</span>}
                            </td>
                            <td className={s.tdAmt}>
                              {r.sysAmt > 0 ? `₱${fmt(r.sysAmt)}` : <span className={s.muted}>—</span>}
                            </td>
                            <td className={`${s.tdAmt} ${r.delta !== 0 ? (r.delta > 0 ? s.deltaPos : s.deltaNeg) : s.deltaZero}`}>
                              {Math.abs(r.delta) < 0.005
                                ? <span className={s.checkmark}>✓</span>
                                : `${r.delta > 0 ? "+" : ""}₱${fmt(r.delta)}`}
                            </td>
                            <DateCell
                              iso={r.sysDateReceived}
                              excelContext={`${r.excelRow?.particulars || ""} ${r.excelRow?.sheetName || ""}`}
                              label="received"
                            />
                            <DateCell
                              iso={r.sysDateRecorded}
                              excelContext={null}
                              label="recorded"
                            />
                            <td className={s.tdMono}>{r.sysId || <span className={s.muted}>—</span>}</td>
                            <td className={s.td}>{r.sysMethod || <span className={s.muted}>—</span>}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              <div className={s.tableFooter}>
                Showing {visibleRows.length} of {comparison.rows.length} records
                {filter !== "all" && (
                  <button className={s.clearFilter} onClick={() => setFilter("all")}>
                    Clear filter
                  </button>
                )}
                {searchQuery && (
                  <button className={s.clearFilter} onClick={() => setSearchQuery("")}>
                    Clear search
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
